/**
 * XML serialisation over a parsed DOM tree, behind the XML Formatter. The widget parses
 * with the browser's `DOMParser`; keeping the walk here lets tests drive it with a
 * small stand-in tree.
 */

const NODE_ELEMENT = 1;
const NODE_TEXT = 3;
const NODE_CDATA = 4;
const NODE_PROCESSING_INSTRUCTION = 7;
const NODE_COMMENT = 8;
const NODE_DOCUMENT_TYPE = 10;

const LINE_BREAK_REGEX = /[\r\n]/;
const XML_DECLARATION_REGEX = /^\s*<\?xml[^?]*\?>/;

const AMP_REGEX = /&/g;
const LT_REGEX = /</g;
const GT_REGEX = />/g;
const QUOT_REGEX = /"/g;

function escapeXmlText(text: string): string {
  return text
    .replace(AMP_REGEX, "&amp;")
    .replace(LT_REGEX, "&lt;")
    .replace(GT_REGEX, "&gt;");
}

function escapeXmlAttribute(value: string): string {
  return escapeXmlText(value).replace(QUOT_REGEX, "&quot;");
}

function serializeAttributes(element: Element): string {
  let result = "";
  for (const attr of Array.from(element.attributes)) {
    result += ` ${attr.name}="${escapeXmlAttribute(attr.value)}"`;
  }
  return result;
}

export function serializeChildren(
  node: Node,
  indent: string,
  depth: number,
  pretty: boolean
): string[] {
  const parts: string[] = [];
  for (const child of Array.from(node.childNodes)) {
    const serialized = serializeNode(child, indent, depth, pretty);
    if (serialized.length > 0) {
      parts.push(serialized);
    }
  }
  return parts;
}

function isTextOnlyElement(element: Element): boolean {
  return (
    element.childNodes.length === 1 &&
    element.childNodes[0].nodeType === NODE_TEXT
  );
}

function serializeElement(
  element: Element,
  indent: string,
  depth: number,
  pretty: boolean
): string {
  const pad = pretty ? indent.repeat(depth) : "";
  const attrs = serializeAttributes(element);
  const tag = element.tagName;
  if (element.childNodes.length === 0) {
    return `${pad}<${tag}${attrs}/>`;
  }
  if (isTextOnlyElement(element)) {
    const text = element.childNodes[0].nodeValue ?? "";
    const content = pretty ? text.trim() : text;
    return `${pad}<${tag}${attrs}>${escapeXmlText(content)}</${tag}>`;
  }
  const children = serializeChildren(element, indent, depth + 1, pretty);
  if (children.length === 0) {
    return `${pad}<${tag}${attrs}/>`;
  }
  const joiner = pretty ? "\n" : "";
  return `${pad}<${tag}${attrs}>${joiner}${children.join(joiner)}${joiner}${pad}</${tag}>`;
}

/**
 * Text next to child elements. Formatting trims it, because each piece goes on its own
 * line. Minifying keeps it exactly, since in mixed content such as
 * `<p>Hello <b>world</b> again</p>` the spaces are part of the content; only the
 * whitespace-only runs that hold a line break (indentation) are dropped.
 */
function serializeText(value: string, pad: string, pretty: boolean): string {
  if (pretty) {
    const text = value.trim();
    return text.length === 0 ? "" : `${pad}${escapeXmlText(text)}`;
  }
  if (value.trim().length === 0 && LINE_BREAK_REGEX.test(value)) {
    return "";
  }
  return escapeXmlText(value);
}

function serializeNode(
  node: Node,
  indent: string,
  depth: number,
  pretty: boolean
): string {
  const pad = pretty ? indent.repeat(depth) : "";
  if (node.nodeType === NODE_ELEMENT) {
    return serializeElement(node as Element, indent, depth, pretty);
  }
  if (node.nodeType === NODE_TEXT) {
    return serializeText(node.nodeValue ?? "", pad, pretty);
  }
  if (node.nodeType === NODE_CDATA) {
    return `${pad}<![CDATA[${node.nodeValue ?? ""}]]>`;
  }
  if (node.nodeType === NODE_COMMENT) {
    return `${pad}<!--${node.nodeValue ?? ""}-->`;
  }
  if (node.nodeType === NODE_PROCESSING_INSTRUCTION) {
    const pi = node as ProcessingInstruction;
    return `${pad}<?${pi.target} ${pi.data}?>`;
  }
  return "";
}

export function countElements(node: Node): number {
  let count = node.nodeType === NODE_ELEMENT ? 1 : 0;
  for (const child of Array.from(node.childNodes)) {
    count += countElements(child);
  }
  return count;
}

export function maxElementDepth(node: Node): number {
  let deepest = 0;
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === NODE_ELEMENT) {
      const childDepth = 1 + maxElementDepth(child);
      if (childDepth > deepest) {
        deepest = childDepth;
      }
    }
  }
  return deepest;
}

const DOCTYPE_START = "<!DOCTYPE";

/** Index just past the end of a prolog item that starts at `from`, or -1 if none does. */
function prologItemEnd(text: string, from: number): number {
  for (const [open, close] of [
    ["<?", "?>"],
    ["<!--", "-->"],
  ]) {
    if (text.startsWith(open, from)) {
      const end = text.indexOf(close, from + open.length);
      return end === -1 ? text.length : end + close.length;
    }
  }
  return -1;
}

/** Index just past the `>` that closes a DOCTYPE starting at `from` (quotes and `[…]` respected). */
function doctypeEnd(text: string, from: number): number {
  let quote = "";
  let depth = 0;
  for (let i = from + DOCTYPE_START.length; i < text.length; i++) {
    const char = text[i];
    if (quote !== "") {
      quote = char === quote ? "" : quote;
    } else if (char === '"' || char === "'") {
      quote = char;
    } else if (char === "[") {
      depth++;
    } else if (char === "]") {
      depth--;
    } else if (char === ">" && depth <= 0) {
      return i + 1;
    }
  }
  return text.length;
}

/**
 * The `<!DOCTYPE …>` declaration exactly as written, internal subset included (the DOM
 * does not expose it), or null when the prolog has none.
 */
export function extractDoctype(input: string): string | null {
  let position = 0;
  while (position < input.length) {
    while (position < input.length && input[position].trim() === "") {
      position++;
    }
    if (input.startsWith(DOCTYPE_START, position)) {
      return input.slice(position, doctypeEnd(input, position));
    }
    const end = prologItemEnd(input, position);
    if (end === -1) {
      return null;
    }
    position = end;
  }
  return null;
}

function doctypeFromNode(node: Node): string {
  const doctype = node as DocumentType;
  const external = doctype.publicId
    ? ` PUBLIC "${doctype.publicId}" "${doctype.systemId}"`
    : (doctype.systemId && ` SYSTEM "${doctype.systemId}"`) || "";
  return `<!DOCTYPE ${doctype.name}${external}>`;
}

/**
 * The whole document as text: the XML declaration and DOCTYPE as written, then every
 * top-level node. `input` is the source the document was parsed from.
 */
export function formatXmlDocument(
  doc: Node,
  input: string,
  indent: string,
  pretty: boolean
): string {
  const joiner = pretty ? "\n" : "";
  const parts: string[] = [];
  const declaration = input.match(XML_DECLARATION_REGEX);
  if (declaration !== null) {
    parts.push(declaration[0].trim());
  }
  const doctype = extractDoctype(input);
  for (const child of Array.from(doc.childNodes)) {
    const text =
      child.nodeType === NODE_DOCUMENT_TYPE
        ? (doctype ?? doctypeFromNode(child))
        : serializeNode(child, indent, 0, pretty);
    if (text.length > 0) {
      parts.push(text);
    }
  }
  return parts.join(joiner);
}
